import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiFetch, apiJson } from '../lib/api'
import PhotoUpload from '../components/PhotoUpload'
import Field from '../components/forms/Field'

interface MeResponse {
  profile: {
    id: string
    email: string
    display_name: string | null
    avatar_url: string | null
    role: string | null
  }
  agent: { id: string } | null
}

interface FormState {
  name: string
  whatsapp: string
  agency_name: string
  fiba_license_number: string
  bio: string
  headline: string
  years_experience: string
}

const initial: FormState = {
  name: '',
  whatsapp: '',
  agency_name: '',
  fiba_license_number: '',
  bio: '',
  headline: '',
  years_experience: '',
}

export default function OnboardingAgent() {
  const navigate = useNavigate()
  const [profile, setProfile] = useState<MeResponse['profile'] | null>(null)
  const [form, setForm] = useState<FormState>(initial)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // Photo upload at setup runs in deferred mode — agents row doesn't exist
  // yet, so /api/upload/photo (requireAgentOwner) would 403 if we tried to
  // upload on file pick. Mirror of OnboardingCoach (commit b872636): hold
  // the resized blob locally, POST it after the agent is created on submit.
  const [photoBlob, setPhotoBlob] = useState<Blob | null>(null)

  useEffect(() => {
    let cancelled = false
    async function bootstrap() {
      try {
        const me = await apiJson<MeResponse>('/api/me')
        if (cancelled) return
        if (me.profile.role !== 'agent') {
          navigate('/onboarding', { replace: true })
          return
        }
        if (me.agent) {
          navigate('/dashboard', { replace: true })
          return
        }
        setProfile(me.profile)
        setForm(f => ({ ...f, name: me.profile.display_name ?? '' }))
        setLoading(false)
      } catch {
        if (cancelled) return
        navigate('/', { replace: true })
      }
    }
    bootstrap()
    return () => {
      cancelled = true
    }
  }, [navigate])

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(f => ({ ...f, [key]: value }))
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setErr(null)
    const yearsTrimmed = form.years_experience.trim()
    const years = yearsTrimmed === '' ? null : Number(yearsTrimmed)
    if (years !== null && !Number.isFinite(years)) {
      setErr('Years of experience must be a number.')
      setSubmitting(false)
      return
    }
    try {
      await apiJson('/api/agent', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name.trim(),
          whatsapp: form.whatsapp.trim() || null,
          agency_name: form.agency_name.trim() || null,
          fiba_license_number: form.fiba_license_number.trim() || null,
          bio: form.bio.trim() || null,
          headline: form.headline.trim(),
          years_experience: years,
        }),
      })
      // Agent row now exists, role='agent', /api/upload/photo will resolve.
      // Best-effort: profile is already saved if the photo POST fails.
      if (photoBlob) {
        try {
          const fd = new FormData()
          fd.append(
            'file',
            new File([photoBlob], 'photo.jpg', { type: 'image/jpeg' }),
          )
          fd.append('entity_type', 'agent')
          const res = await apiFetch('/api/upload/photo', {
            method: 'POST',
            body: fd,
          })
          if (!res.ok) {
            console.warn(
              '[onboarding-agent] photo upload failed post-create',
              res.status,
            )
          }
        } catch (uploadErr) {
          console.warn(
            '[onboarding-agent] photo upload threw post-create',
            uploadErr,
          )
        }
      }
      navigate('/dashboard', { replace: true })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'unknown_error')
      setSubmitting(false)
    }
  }

  if (loading || !profile) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-hoop-bg">
        <p className="text-hoop-text-3">Loading…</p>
      </main>
    )
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-6 bg-hoop-bg">
      <form onSubmit={submit} className="max-w-xl w-full space-y-5">
        <header>
          <h1 className="text-3xl font-bold text-hoop-text">
            Set up your agent profile
          </h1>
          <p className="text-hoop-text-2 mt-1 text-sm">
            This becomes the "Represented by" card on every player you add.
          </p>
        </header>

        <PhotoUpload
          entityType="agent"
          currentPhotoUrl={null}
          onChange={() => {}}
          deferred
          onFilePicked={setPhotoBlob}
          label="Profile photo (optional)"
        />

        <Field label="Your name" required>
          <input
            type="text"
            required
            value={form.name}
            onChange={e => update('name', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Headline" required hint="One line — e.g. 'Independent agent · SE Asia'">
          <input
            type="text"
            required
            value={form.headline}
            onChange={e => update('headline', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Years of experience">
          <input
            type="number"
            min={0}
            step={1}
            value={form.years_experience}
            onChange={e => update('years_experience', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Agency name" hint="Leave blank if you're independent.">
          <input
            type="text"
            value={form.agency_name}
            onChange={e => update('agency_name', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="WhatsApp" hint="Include country code.">
          <input
            type="text"
            value={form.whatsapp}
            onChange={e => update('whatsapp', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field
          label="FIBA license number"
          hint="Optional. Shows a 'FIBA Licensed' badge with a link to FIBA's agent search so coaches can verify. Leave blank if you don't have one."
        >
          <input
            type="text"
            value={form.fiba_license_number}
            onChange={e => update('fiba_license_number', e.target.value)}
            placeholder="e.g. 2024-001234"
            className={inputClass}
          />
        </Field>

        <Field label="Bio">
          <textarea
            rows={4}
            value={form.bio}
            onChange={e => update('bio', e.target.value)}
            className={inputClass}
          />
        </Field>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="bg-hoop-primary text-hoop-primary-fg px-6 py-3 rounded-lg font-medium hover:opacity-90 disabled:opacity-40"
          >
            {submitting ? 'Saving…' : 'Create agent profile'}
          </button>
          {err && (
            <p className="text-sm text-hoop-red">{err}</p>
          )}
        </div>
      </form>
    </main>
  )
}

const inputClass =
  'w-full rounded-md border border-hoop-border bg-hoop-surface px-3 py-2 text-sm text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent'
